import React from 'react';
import { Link } from '@fluentui/react';
import { getVpnLockOverlayStyles } from './VpnLockOverlay.styles';

export interface VpnLockOverlayProps {
    /** Whether to show the overlay */
    isVisible: boolean;
    /** Callback when user clicks retry (optional) */
    onRetry?: () => Promise<void>;
    /** Error message to display (optional) */
    errorMessage?: string;
    /** Title text (default: "VPN REQUIRED") */
    title?: string;
    /** Description text */
    description?: string;
    /** Whether a health check is currently in progress */
    isChecking?: boolean;
    /** Whether this is the initial connection attempt */
    isInitialCheck?: boolean;
    /** Application name to display in the message */
    appName?: string;
    /** Documentation link URL */
    docLink?: string;
    /** Documentation link text */
    docLinkText?: string;
}

/**
 * VPN Lock Overlay Component
 * 
 * Full-page overlay that blocks all UI interactions when VPN connection is lost.
 * Follows Microsoft's standard VPN required design pattern.
 * 
 * Usage:
 * ```tsx
 * <VpnLockOverlay
 *     isVisible={isDisconnected}
 *     appName="CASE Portal"
 * />
 * ```
 */
export const VpnLockOverlay: React.FC<VpnLockOverlayProps> = ({
    isVisible,
    title = "VPN REQUIRED",
    appName = "This application",
    docLink = "https://aka.ms/DocVPN",
    docLinkText = "aka.ms/DocVPN"
}) => {
    const styles = getVpnLockOverlayStyles();

    if (!isVisible) {
        return null;
    }

    return (
        <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="vpn-lock-title">
            <div className={styles.content}>
                {/* Header */}
                <div className={styles.header}>
                    <h1 id="vpn-lock-title" className={styles.headerTitle}>
                        {title}
                    </h1>
                </div>

                {/* Body */}
                <div className={styles.body}>
                    <p className={styles.paragraph}>
                        {appName} now requires an allowed network. Please connect a VPN and refresh this page.
                    </p>

                    <p className={styles.paragraph}>
                        Most users should use Azure VPN (MSFT-AzVPN-*) or access from Corpnet, a SAW, or a cloud Dev Box.
                    </p>

                    <p className={styles.paragraph}>
                        Mobile devices are not supported.
                    </p>

                    <p className={styles.lastParagraph}>
                        Please visit{' '}
                        <Link href={docLink.startsWith('https://') ? docLink : 'https://aka.ms/DocVPN'} target="_blank" rel="noopener noreferrer" className={styles.link}>
                            {docLinkText}
                        </Link>
                        {' '}for details including other cases.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default VpnLockOverlay;
